/**
 * Pure handler functions for dependency operations.
 * These functions contain the data transformation logic extracted from DependenciesGrid.tsx
 * to enable easier testing and better separation of concerns.
 */

import {
  DependencyData,
  DependencyCheckClient,
  DependencyCheckClientAgent,
  RestorePendingDependencies,
  CheckIsReadyRequest,
  toCheckIsReadyRequest,
} from "./types";

/**
 * Filters out rows with null or undefined id.
 * This is a common validation step before processing selected rows.
 */
export function filterValidRows(rows: DependencyData[]): DependencyData[] {
  return rows.filter((r) => r.id != null);
}

/**
 * Creates CheckIsReadyRequest payloads from dependency data.
 * Used by the "Check Is Ready" action.
 */
export function createCheckIsReadyPayload(
  rows: DependencyData[]
): CheckIsReadyRequest[] {
  return rows.map(toCheckIsReadyRequest);
}

/**
 * Creates DependencyCheckClient payloads from dependency data.
 * Used by the "Force Client" action.
 * 
 * Maps:
 * - partitionKey → dependencyKey
 * - metaData["Client"] → client
 */
export function createForceClientPayload(
  rows: DependencyData[]
): DependencyCheckClient[] {
  return rows.map(
    (r) => new DependencyCheckClient(r.partitionKey, r.metaData["Client"])
  );
}

/**
 * Creates DependencyCheckClientAgent payloads from dependency data.
 * Used by the "Force ClientAgent" action.
 * 
 * Maps:
 * - partitionKey → dependencyKey
 * - metaData["ClientAgent"] → clientAgent
 */
export function createForceClientAgentPayload(
  rows: DependencyData[]
): DependencyCheckClientAgent[] {
  return rows.map(
    (r) =>
      new DependencyCheckClientAgent(r.partitionKey, r.metaData["ClientAgent"])
  );
}

/**
 * Counts rows whose metaData[field] is missing or blank. Force Client / Force ClientAgent is
 * disabled while any selected row has no target: forcing it would force the whole key for
 * every calculation in every tenant (PMDEV-3736).
 */
export function countRowsMissingMetaData(
  rows: DependencyData[],
  field: "Client" | "ClientAgent"
): number {
  return rows.filter((r) => {
    const value = r.metaData?.[field];
    return value == null || String(value).trim() === "";
  }).length;
}

/**
 * Restore type parameter used by restore operations.
 */
export type RestoreType = "AssetDV" | "LiabilityDV" | "DailyMetric" | "All";

/**
 * Creates RestorePendingDependencies payloads for "Restore by Scheme" action.
 * 
 * Maps:
 * - metaData["AppName"] → appName
 * - metaData["SchemeId"] → schemeId
 * - metaData["ValuationDate"] → valuationDate
 * - clientAgent and client are null
 */
export function createRestoreBySchemePayload(
  rows: DependencyData[],
  restoreType: RestoreType
): RestorePendingDependencies[] {
  return rows.map(
    (r) =>
      new RestorePendingDependencies(
        r.metaData["AppName"],
        null, // clientAgent
        null, // client
        r.metaData["SchemeId"],
        restoreType,
        r.metaData["ValuationDate"]
      )
  );
}

/**
 * Creates RestorePendingDependencies payloads for "Restore by Client" action.
 * 
 * Maps:
 * - metaData["AppName"] → appName
 * - metaData["Client"] → client
 * - metaData["ValuationDate"] → valuationDate
 * - clientAgent and schemeId are null
 */
export function createRestoreByClientPayload(
  rows: DependencyData[],
  restoreType: RestoreType
): RestorePendingDependencies[] {
  return rows.map(
    (r) =>
      new RestorePendingDependencies(
        r.metaData["AppName"],
        null, // clientAgent
        r.metaData["Client"],
        null, // schemeId
        restoreType,
        r.metaData["ValuationDate"]
      )
  );
}

/**
 * Creates RestorePendingDependencies payloads for "Restore by ClientAgent" action.
 * 
 * Maps:
 * - metaData["AppName"] → appName
 * - metaData["ClientAgent"] → clientAgent
 * - metaData["ValuationDate"] → valuationDate
 * - client and schemeId are null
 */
export function createRestoreByClientAgentPayload(
  rows: DependencyData[],
  restoreType: RestoreType
): RestorePendingDependencies[] {
  return rows.map(
    (r) =>
      new RestorePendingDependencies(
        r.metaData["AppName"],
        r.metaData["ClientAgent"],
        null, // client
        null, // schemeId
        restoreType,
        r.metaData["ValuationDate"]
      )
  );
}

/**
 * Creates RestorePendingDependencies payloads for "Restore by Environment" action.
 * Only includes appName, type, and valuationDate - no scheme/client/clientAgent filtering.
 * 
 * Maps:
 * - metaData["AppName"] → appName
 * - metaData["ValuationDate"] → valuationDate
 * - clientAgent, client, and schemeId are null
 */
export function createRestoreByEnvPayload(
  rows: DependencyData[],
  restoreType: RestoreType
): RestorePendingDependencies[] {
  return rows.map(
    (r) =>
      new RestorePendingDependencies(
        r.metaData["AppName"],
        null, // clientAgent
        null, // client
        null, // schemeId
        restoreType,
        r.metaData["ValuationDate"]
      )
  );
}

/**
 * Known application names for the "Restore All" operation.
 * These are the environments that will be restored when clicking "Restore All".
 */
export const RESTORE_ALL_APP_NAMES = [
  "PFaroeDemo",
  "PFaroePunt",
  "PFaroeChan",
  "PFaroe",
  "Skyval",
] as const;

/**
 * Creates RestorePendingDependencies payloads for "Restore All" action.
 * This creates a fixed set of payloads for all known environments.
 * Does not depend on selected rows.
 */
export function createRestoreAllPayload(): RestorePendingDependencies[] {
  return RESTORE_ALL_APP_NAMES.map(
    (appName) =>
      new RestorePendingDependencies(
        appName,
        null, // clientAgent
        null, // client
        null, // schemeId
        "All",
        null // valuationDate
      )
  );
}
